import { useState } from "react";
import { trackEvent } from "../utils/analytics.js";

// Share links for a page (used on event details). Plain share URLs, no
// platform SDKs or tracking scripts. On devices with the Web Share API
// (most phones) a "Share…" button opens the native share sheet, which covers
// Instagram, Messenger, Telegram and others that have no web share link.
// Analytics: GA4 recommended event `share` (method, content_type, item_id).

const buildLinks = (url, text) => {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(text);
  return [
    { method: "whatsapp", label: "WhatsApp", icon: "bi-whatsapp", href: `https://api.whatsapp.com/send?text=${t}%20${u}` },
    { method: "facebook", label: "Facebook", icon: "bi-facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${u}` },
    { method: "x", label: "X", icon: "bi-twitter-x", href: `https://x.com/intent/post?text=${t}&url=${u}` },
    { method: "linkedin", label: "LinkedIn", icon: "bi-linkedin", href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}` },
    { method: "email", label: "Email", icon: "bi-envelope", href: `mailto:?subject=${t}&body=${t}%0A%0A${u}`, sameTab: true },
  ];
};

const ShareButtons = ({ url, title, text = title, contentType = "event", itemId }) => {
  const [copied, setCopied] = useState(false);
  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const track = (method) => trackEvent("share", { method, content_type: contentType, item_id: itemId });

  const nativeShare = async () => {
    try {
      await navigator.share({ title, text, url });
      track("native");
    } catch {
      // Cancelled by the user - nothing to do.
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Older browsers: select-and-copy fallback.
      const input = document.createElement("input");
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      input.remove();
    }
    setCopied(true);
    track("copy_link");
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="share-buttons" data-share>
      <p className="fw-semibold mb-2" id="share-heading">
        Share this event
      </p>
      <ul className="list-unstyled d-flex flex-wrap gap-2 mb-0" aria-labelledby="share-heading">
        {canNativeShare && (
          <li>
            <button type="button" className="share-btn share-native" onClick={nativeShare}>
              <i className="bi bi-share" aria-hidden="true"></i>
              <span className="visually-hidden">Share using your device</span>
            </button>
          </li>
        )}
        {buildLinks(url, text).map((link) => (
          <li key={link.method}>
            <a
              href={link.href}
              className={`share-btn share-${link.method}`}
              {...(!link.sameTab && { target: "_blank", rel: "nofollow noopener noreferrer" })}
              onClick={() => track(link.method)}
              title={`Share on ${link.label}`}
            >
              <i className={`bi ${link.icon}`} aria-hidden="true"></i>
              <span className="visually-hidden">
                Share on {link.label}
                {!link.sameTab && " (opens in a new tab)"}
              </span>
            </a>
          </li>
        ))}
        <li>
          <button type="button" className="share-btn share-copy" onClick={copyLink} title="Copy link">
            <i className={`bi ${copied ? "bi-check-lg" : "bi-link-45deg"}`} aria-hidden="true"></i>
            <span className="visually-hidden">Copy link</span>
          </button>
        </li>
      </ul>
      <p className="small text-success mt-2 mb-0" role="status" aria-live="polite">
        {copied ? "Link copied" : ""}
      </p>
    </div>
  );
};

export default ShareButtons;
